import { GetSubsidyResponse } from '@hc-frontend/services';
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';

import { Answers, AnswersObject } from './constants';

type SubsidyCalculatorContextType = {
  step: number;
  setStep: Dispatch<SetStateAction<number>>;
  answers: AnswersObject;
  setAnswer: <K extends Answers>(key: K, value: AnswersObject[K]) => void;
  subsidy?: GetSubsidyResponse;
  setSubsidy: Dispatch<SetStateAction<GetSubsidyResponse | undefined>>;
  reset: () => void;
};

const SubsidyCalculatorContext = createContext<
  SubsidyCalculatorContextType | undefined
>(undefined);

export const SubsidyCalculatorProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<AnswersObject>({});
  const [subsidy, setSubsidy] = useState<GetSubsidyResponse>();

  const setAnswer = (key: string, value: unknown) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setSubsidy(undefined);
  };

  return (
    <SubsidyCalculatorContext.Provider
      value={{ step, setStep, answers, setAnswer, subsidy, setSubsidy, reset }}
    >
      {children}
    </SubsidyCalculatorContext.Provider>
  );
};

export const useSubsidyCalculator = () => {
  const context = useContext(SubsidyCalculatorContext);

  if (context === undefined) {
    throw new Error(
      'useSubsidyCalculator must be used within a SubsidyCalculatorProvider',
    );
  }

  return context;
};
